import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
  MenuItem,
  Avatar,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import categoryService from '../../../services/categoryService';
import { Category as CategoryIcon } from '@mui/icons-material';

const CategoryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    slug: '',
    parentId: '',
    isActive: true,
    sellOnline: true,
    displayOrder: 0,
  });

  const [availableCategories, setAvailableCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Categories are global (shared by every store): no store to pick, one list of possible parents.
  useEffect(() => {
    fetchParentCategories();
    if (isEdit) fetchCategory();
  }, [id]);

  const fetchParentCategories = async () => {
    try {
      const data = await categoryService.getRootCategories();
      setAvailableCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchCategory = async () => {
    try {
      const data = await categoryService.getCategoryById(id);
      setFormData({
        name: data.name,
        description: data.description || '',
        slug: data.slug || '',
        parentId: data.parentId || '',
        isActive: data.isActive,
        sellOnline: data.sellOnline !== false,
        displayOrder: data.displayOrder || 0,
      });
    } catch (error) {
      toast.error(t('categories.fetchError'));
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEdit) {
        await categoryService.updateCategory(id, formData);
        toast.success(t('categories.updateSuccess'));
      } else {
        await categoryService.createCategory(formData);
        toast.success(t('categories.createSuccess'));
      }
      navigate('/categories');
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.saveError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {isEdit ? t('categories.editCategory') : t('categories.createCategory')}
      </Typography>

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Category Name */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('categories.category')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>

              {/* Slug */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('categories.slug')}
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  helperText={t('categories_ext.slugHelper')}
                  placeholder={t('categories_ext.slugPlaceholder')}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('common.description')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={3}
                />
              </Grid>

              {/* Parent Category */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label={t('categories.parentCategory')}
                  value={formData.parentId}
                  onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
                  helperText={t('categories_ext.parentHelper')}
                >
                  <MenuItem value="">
                    <em>{t('categories.noParent')}</em>
                  </MenuItem>
                  {availableCategories
                    .filter(cat => !isEdit || cat.id !== id)
                    .map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>

              {/* Display Order */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('categories_ext.displayOrder')}
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                  helperText={t('categories_ext.displayOrderHelper')}
                />
              </Grid>

              {/* Image URL */}
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label={t('categories_ext.imageUrl')}
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                  helperText={t('categories_ext.imageUrlHelper')}
                />
              </Grid>

              {/* Image Preview */}
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  {formData.imageUrl ? (
                    <Avatar
                      src={formData.imageUrl}
                      variant="rounded"
                      sx={{ width: 100, height: 100 }}
                    >
                      <CategoryIcon sx={{ fontSize: 50 }} />
                    </Avatar>
                  ) : (
                    <Avatar
                      variant="rounded"
                      sx={{ width: 100, height: 100, bgcolor: 'primary.main' }}
                    >
                      <CategoryIcon sx={{ fontSize: 50 }} />
                    </Avatar>
                  )}
                </Box>
              </Grid>

              {/* Active Status */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label={t('common.active')}
                />
              </Grid>

              {/* Sell on the website */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.sellOnline}
                      onChange={(e) => setFormData({ ...formData, sellOnline: e.target.checked })}
                    />
                  }
                  label={t('sellOnline.label')}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('sellOnline.helpCategory')}
                </Typography>
              </Grid>

              {/* Form Actions */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button onClick={() => navigate('/categories')} disabled={loading}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" variant="contained" disabled={loading}>
                    {loading ? t('common.save') + '...' : isEdit ? t('categories.editCategory') : t('categories.createCategory')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CategoryForm;
